import { useState } from 'react';
import NoteModal from '~/components/NoteModal';
import {
  useAddNoteMutation,
  useDeleteFavoriteMutation,
} from '~/redux/api/userApiSlice';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import {
  SavedPropertyFilterTabType,
  SavedPropertyItemType,
} from '~/types/activitiesTypes';
import { removeUserFavoriteId } from '~/utils/favorites';
import SavedPropertyCard from '~/modules/activities/pages/Favorites/FavoriteCard';
import CompoundCard from '~/modules/activities/pages/Favorites/CompoundCard';
import ShareModel from '~activities/pages/Favorites/ShareModel';
import Report from '~activities/pages/Favorites/Report';
import SharePropertiesModal from '~activities/pages/Favorites/SharePropertiesModal';

const FavoritesCardList = ({
  listings,
  setListings,
  setFavoritesCount,
  setNotesCount,
  sharedFavoritesUrl,
  sharedNotesUrl,
  isSheared,
  activeTab,
}: {
  listings: SavedPropertyItemType[];
  setFavoritesCount: React.Dispatch<React.SetStateAction<number>>;
  setNotesCount: React.Dispatch<React.SetStateAction<number>>;
  sharedFavoritesUrl: string | null;
  sharedNotesUrl: string | null;
  isSheared?: boolean;
  setListings: React.Dispatch<React.SetStateAction<SavedPropertyItemType[]>>;
  activeTab?: SavedPropertyFilterTabType;
}) => {
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const [note, setNote] = useState('');
  const [listingId, setListingId] = useState(0);
  const dispatch = useAppDispatch();
  const [addNote] = useAddNoteMutation();
  const [shareRoute, setShareRoute] = useState('');

  const handelDeleteFavorite = async (item: SavedPropertyItemType) => {
    const listingId = item.listing.id;
    const note = item.note;

    const updatedListings = listings.filter(
      (item) => item.listing.id !== listingId,
    );
    setListings(updatedListings);
    const res = await deleteFavorite(listingId);
    if ('data' in res) {
      removeUserFavoriteId(listingId);
      setFavoritesCount((prev) => prev - 1);
      if (note) setNotesCount((prev) => prev - 1);
    }
    setNote('');
    setListingId(0);
  };

  const handleOpenNoteModal = (item: SavedPropertyItemType) => {
    setNote(item.note || '');
    setListingId(item.listing.id);
    dispatch(openModal('note'));
  };

  const handleDeleteNote = async (id: number) => {
    setListings((prev) =>
      activeTab === 'notes'
        ? prev.filter((item) => item.listing.id !== id)
        : prev.map((item) =>
            item.listing.id === id ? { ...item, note: '' } : item,
          ),
    );
    setNotesCount((prev) => prev - 1);
    await addNote({ listingId: id, note: '' });
    dispatch(closeModal('deleteFavorite'));
  };

  return (
    <div className="flex flex-col gap-2x">
      {listings.map((item, index) => {
        const isCompound =
          Boolean(item.listing.is_project_or_unit) && !item.listing.has_parent;
        const CardComponent = isCompound ? CompoundCard : SavedPropertyCard;

        return (
          <CardComponent
            key={index}
            item={item}
            handelDeleteFavorite={() => handelDeleteFavorite(item)}
            handleOpenNoteModal={() => handleOpenNoteModal(item)}
            handleDeleteNote={() => handleDeleteNote(item.listing.id)}
            isSheared={isSheared}
            setShareRoute={setShareRoute}
          />
        );
      })}
      <NoteModal
        listingId={listingId}
        note={note}
        setNote={(val: string) => {
          setNote(val);
          setListings((prev) => {
            if (activeTab === 'notes' && !val) {
              return prev.filter((item) => item.listing.id !== listingId);
            }
            return prev.map((item) =>
              item.listing.id === listingId ? { ...item, note: val } : item,
            );
          });
          setNotesCount((prev) => {
            const listing = listings.find(
              (item) => item.listing.id === listingId,
            );
            if (listing) {
              if (!listing.note && val) {
                return prev + 1;
              } else if (listing.note && !val) {
                return prev - 1;
              }
            }
            return prev;
          });
        }}
      />
      <ShareModel link={shareRoute} />
      <Report />
      <SharePropertiesModal
        sharedFavoritesUrl={sharedFavoritesUrl}
        sharedNotesUrl={sharedNotesUrl}
      />
    </div>
  );
};

export default FavoritesCardList;
